import { updateBeamUrl } from '@utils/index';
import { unwrap } from '@utils/unwrap/unwrap';
import DataConverter from './DataConverter.model';
import {
  AssociatedWith,
  DocumentAddedBy,
  IDocument,
  IDocumentTable,
  IDocumentTableApiModel,
} from './Document.model';
import Manufacturer from './Manufacturer.model';
import Product from './Product.model';

function getDocumentAddedBy(model: IDocumentTableApiModel): DocumentAddedBy {
  if (model.ownerStudioId) return DocumentAddedBy.internal;
  return DocumentAddedBy.beam;
}
function getAssociatedWith(model: IDocumentTableApiModel) {
  if (model.isGlobal) return AssociatedWith.global;
  return model.associatedWith;
}
function getDocumentAddedByName(document: IDocumentTable) {
  if (document.addedBy === DocumentAddedBy.beam) return 'Beam';

  return document.addedByUser?.name || 'Unknown';
}

function getDocumentAssociatedWithName(document: IDocumentTable) {
  if (document.associatedWith === AssociatedWith.manufacturer) {
    return document.manufacturer?.name || 'Unknown';
  }
  if (document.associatedWith === AssociatedWith.product) {
    return document.product?.name || 'Unknown';
  }
  return 'Global';
}
function getDocumentAssociatedWithSource(document: IDocumentTable) {
  if (document.addedBy === DocumentAddedBy.internal) return 'Internal';
  if (document.associatedWith === AssociatedWith.manufacturer) {
    return document.manufacturer?.name || 'Unknown';
  }
  return document.product?.manufacturer?.name || 'Unknown';
}
class DocumentTableConverter extends DataConverter<
  IDocumentTableApiModel,
  IDocumentTable,
  IDocument
> {
  fromApiModel(model: IDocumentTableApiModel): IDocumentTable {
    const {
      createdAt,
      updatedAt,
      manufacturers,
      products,
      file,
      releaseDate,
      ...rest
    } = model;

    const createdAtDate = new Date(createdAt);
    const updatedAtDate = new Date(updatedAt);
    const releaseDateDate = releaseDate ? new Date(releaseDate) : null;
    const addedBy = getDocumentAddedBy(model);
    const associatedWith = getAssociatedWith(model);

    const manufacturer = manufacturers?.at(0)
      ? Manufacturer.fromApiModel(manufacturers[0])
      : undefined;
    const product = products?.at(0)
      ? Product.fromApiModel(products[0])
      : undefined;
    const addedByUser = unwrap(model.addedByUser);

    const manufacturerId = manufacturer?.id ?? undefined;
    const uri = model.uri ?? file?.uri;
    const updatedUri = uri ? updateBeamUrl(uri) : null;
    const productManufacturerId = product?.manufacturerId;
    // Mutates rest to give document
    const document: IDocumentTable = Object.assign(rest, {
      addedBy,
      manufacturer,
      product,
      associatedWith,
      addedByUser,
      manufacturerId,
      productManufacturerId,
      releaseDate: releaseDateDate,
      createdAt: createdAtDate,
      updatedAt: updatedAtDate,
      uri: updatedUri,
      addedByName: '',
      associatedWithName: '',
      associatedWithSource: '',
    });

    document.associatedWithName = getDocumentAssociatedWithName(document);
    document.associatedWithSource = getDocumentAssociatedWithSource(document);
    document.addedByName = getDocumentAddedByName(document);

    return document;
  }
}

// Field configs
export const documentsFieldNameDictionary = {
  label: 'Document',
  createdAt: 'Date Added',
  associatedWithName: 'Associated To',
  associatedWithSource: 'Source',
  addedByName: 'Added by',
};
export const documentsSortDictionary = {
  label: 'label',
  createdAt: 'createdAt',
  associatedWithName: 'associatedWithName',
  associatedWithSource: 'associatedWithSource',
  addedByName: 'addedByName',
};
export type DocumentsFieldConfigurationTypeOptions =
  keyof typeof documentsFieldNameDictionary;
export default new DocumentTableConverter();
